import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer, createMigrate } from 'redux-persist';
import { combineReducers } from 'redux';

import authReducer from './slices/authSlice';
import reviewReducer from './slices/reviewSlice';
import offlineReducer from './slices/offlineSlice';
import vendorEmailReducer from './slices/vendorEmailSlice';
import preferencesReducer from './slices/preferencesSlice';
import { createPersistStorage } from './persistStorage';

const persistStorage = createPersistStorage();

// Migration configuration for handling version changes
const migrations = {
  0: (state: any) => {
    // Initial state migration
    return {
      ...state,
      _persist: {
        version: 0,
        rehydrated: true,
      },
    };
  },
  1: (state: any) => {
    // Migration for v1: Add cache metadata to offline slice
    return {
      ...state,
      offline: {
        ...state.offline,
        cacheMetadata: {
          templates: { lastSync: null, ttl: 24 * 60 * 60 * 1000 }, // 24 hours
          categories: { lastSync: null, ttl: 24 * 60 * 60 * 1000 }, // 24 hours
          items: { lastSync: null, ttl: 24 * 60 * 60 * 1000 }, // 24 hours
          rooms: { lastSync: null, ttl: 6 * 60 * 60 * 1000 }, // 6 hours
          reviews: { lastSync: null, ttl: 1 * 60 * 60 * 1000 }, // 1 hour
        },
        syncProgress: {
          isRunning: false,
          currentOperation: null,
          totalOperations: 0,
          completedOperations: 0,
          errors: [],
        },
      },
      _persist: {
        version: 1,
        rehydrated: true,
      },
    };
  },
  2: (state: any) => {
    // Migration for v2: Add deletedReviewIds and onDemandItems to review slice
    return {
      ...state,
      review: {
        ...state.review,
        deletedReviewIds: state.review?.deletedReviewIds || [],
        onDemandItems: state.review?.onDemandItems || {},
      },
      _persist: {
        version: 2,
        rehydrated: true,
      },
    };
  },
};

// Auth slice persistence config (exclude sensitive data)
const authPersistConfig = {
  key: 'auth',
  storage: persistStorage,
  blacklist: ['token'], // Don't persist auth token for security
  version: 2,
  migrate: createMigrate(migrations, { debug: __DEV__ }),
};

// Review slice persistence config (persist essential review data)
const reviewPersistConfig = {
  key: 'review',
  storage: persistStorage,
  whitelist: ['reviews', 'templates'], // Only persist reviews and templates
  version: 2,
  migrate: createMigrate(migrations, { debug: __DEV__ }),
};

// Offline slice persistence config (persist all offline data)
const offlinePersistConfig = {
  key: 'offline',
  storage: persistStorage,
  version: 2,
  migrate: createMigrate(migrations, { debug: __DEV__ }),
};

// Preferences slice persistence config (persist all preferences)
const preferencesPersistConfig = {
  key: 'preferences',
  storage: persistStorage,
  version: 2,
  migrate: createMigrate(migrations, { debug: __DEV__ }),
};

const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  review: persistReducer(reviewPersistConfig, reviewReducer),
  offline: persistReducer(offlinePersistConfig, offlineReducer),
  vendorEmails: vendorEmailReducer, // Not persisted
  preferences: persistReducer(preferencesPersistConfig, preferencesReducer),
});

// Rehydration handling
let rehydrationPromise: Promise<void> | null = null;
let rehydrationResolve: (() => void) | null = null;

const handleRehydration = () => {
  if (!rehydrationPromise) {
    rehydrationPromise = new Promise<void>((resolve) => {
      rehydrationResolve = resolve;
    });
  }
  return rehydrationPromise;
};

const rehydrationMiddleware = (store: any) => (next: any) => (action: any) => {
  // Handle rehydration completion
  if (action.type === 'persist/REHYDRATE') {
    // Check if all slices have been rehydrated
    const state = store.getState();
    const rehydratedSlices = ['auth', 'review', 'offline', 'preferences'];
    const allRehydrated = rehydratedSlices.every(slice =>
      state[slice]?._persist?.rehydrated === true
    );

    if (allRehydrated && rehydrationResolve) {
      rehydrationResolve();
      rehydrationResolve = null;
    }
  }

  return next(action);
};

const serializableCheck = __DEV__
  ? false
  : {
      ignoredActions: [
        'persist/PERSIST',
        'persist/REHYDRATE',
        'persist/PAUSE',
        'persist/PURGE',
        'persist/REGISTER',
      ],
      ignoredActionsPaths: ['meta.arg', 'payload.timestamp'],
      ignoredPaths: ['_persist'],
    };

const immutableCheck = __DEV__
  ? false
  : {
      ignoredPaths: ['_persist'],
    };

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck,
      immutableCheck,
    }).concat(rehydrationMiddleware),
});

export const persistor = persistStore(store);

// Export rehydration promise for app initialization
export const waitForRehydration = handleRehydration;

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
